import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useJoistLeads,
  usePromoteAllJoistLeads,
  usePromoteJoistLead,
} from '@/hooks/useJoistLeads'
import type { JoistDuplicateLink } from '@/types/api'

interface PromoteJoistDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Scopes the prompt to the batch that was just imported */
  scanId: string | null
  /** Reports leads that were enriched rather than created, so the page can list them */
  onDuplicates?: (links: JoistDuplicateLink[]) => void
}

export function PromoteJoistDialog({
  open,
  onOpenChange,
  scanId,
  onDuplicates,
}: PromoteJoistDialogProps) {
  // Driven off the server rather than a client-side array: promotion_status is the
  // source of truth, and it can change under us if someone else promotes concurrently.
  const { data, isLoading } = useJoistLeads(
    { promotion_status: 'pending', scan_id: scanId ?? undefined, limit: 1, sort_order: 'asc' },
    open && Boolean(scanId)
  )

  const promoteOne = usePromoteJoistLead()
  const promoteAll = usePromoteAllJoistLeads()

  const remaining = data?.total ?? 0
  const next = data?.items?.[0]
  const busy = promoteOne.isPending || promoteAll.isPending

  // Nothing left to offer - close rather than showing an empty prompt. In an effect,
  // not the render body, so we never set state while rendering.
  useEffect(() => {
    if (open && !isLoading && !busy && remaining === 0) {
      onOpenChange(false)
    }
  }, [open, isLoading, busy, remaining, onOpenChange])

  function handleYesOne() {
    if (!next) return
    // Deliberately stays open: the refetched count drives the next question, and the
    // effect above closes it once the batch is exhausted.
    // `?? []` guards against an older backend that predates the duplicates field.
    promoteOne.mutate(next.id, {
      onSuccess: (data) => onDuplicates?.(data.duplicates ?? []),
    })
  }

  function handleYesAll() {
    promoteAll.mutate(scanId ?? undefined, {
      onSuccess: (data) => {
        onDuplicates?.(data.duplicates ?? [])
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Insert the imported leads?</DialogTitle>
          <DialogDescription>
            Do you want to insert the newly imported leads from Joist into leads?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          <p data-testid="promote-remaining">
            {remaining === 1 ? '1 lead remaining' : `${remaining} leads remaining`}
          </p>
          {next && (
            <p className="text-muted-foreground" data-testid="promote-next">
              Next: {next.first_name} {next.last_name}
              {next.email ? ` · ${next.email}` : next.phone ? ` · ${next.phone}` : ''}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            No
          </Button>
          <Button onClick={handleYesOne} disabled={busy || !next}>
            {promoteOne.isPending ? 'Inserting...' : 'Yes (1)'}
          </Button>
          <Button variant="secondary" onClick={handleYesAll} disabled={busy || remaining === 0}>
            {promoteAll.isPending ? 'Inserting...' : 'Yes All'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
